<script setup lang="ts">
import { computed } from "vue"
import { useQuery } from "@tanstack/vue-query"

import { ExtendedConvention, getConvention } from "@/models/convention.ts"
import { ConventionUser } from "@/models/convention_user.ts"

import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"

import ConventionUsersCard from "@/views/main/ConventionsView/components/ConventionUsersCard.vue"
import ConventionInfo from "@/views/main/ConventionsView/components/ConventionInfo.vue"

const props = defineProps<{
  id?: string | null
}>()

const emits = defineEmits(["refreshConvention"])

const conventionActiveUsersCount = computed(() => {
  return convention.value?.users?.filter((user) => user.status !== "deactivated").length || 0
})

const { data: convention, refetch: refetchConvention } = useQuery({
  queryKey: computed(() => ["conventions", props.id]),
  queryFn: ({ signal }) => getConvention({ signal: signal, id: props.id }),
  placeholderData: () => ({}) as unknown as ExtendedConvention
})

const onUpdateUserSuccess = (_conventionUser: ConventionUser) => {
  refetchConvention()
}
</script>

<template>
  <div class="container flex flex-row" v-if="convention">
    <div class="w-2/3">
      <Card class="flex-0 my-5">
        <CardHeader class="pb-3">
          <CardTitle>{{ $t("conventions.convention") }}: {{ convention.name }}</CardTitle>
          <CardDescription class="text-balance leading-relaxed">
            {{ convention.description }}
          </CardDescription>
        </CardHeader>
      </Card>

      <ConventionUsersCard
        class="mb-5"
        :convention="convention"
        :active-users-count="conventionActiveUsersCount"
        @create-user-success="onUpdateUserSuccess"
        @update-user-success="onUpdateUserSuccess" />
    </div>

    <div class="my-5 ml-5 w-1/3">
      <ConventionInfo :convention="convention" :active-users-count="conventionActiveUsersCount" />
    </div>
  </div>
</template>

<style scoped></style>
